import { useEffect, useRef, useState } from 'react';

interface ProductSectionProps {
  lang: 'pt' | 'en';
}

const content = {
  pt: {
    label: 'SERVIÇOS',
    headline: 'O que entregamos',
    services: [
      {
        titleLine1: 'Landing Pages',
        titleLine2: 'que Convertem',
        description:
          'Não construímos só páginas bonitas. Criamos sistemas de conversão baseados em UX e psicologia do utilizador. Cada elemento existe para facilitar a decisão de contacto e transformar visitantes em clientes.',
        details: [
          'Página pronta em 7 a 14 dias: copy, design e implementação incluídos',
          'Desenhada com princípios de UX, psicologia cognitiva e CRO',
          'Optimizada para velocidade, mobile e conversão',
        ],
      },
      {
        titleLine1: 'Automação',
        titleLine2: 'que Trabalha por Ti',
        description:
          "Email, SMS e WhatsApp a funcionar 24/7.\nLeads captados, qualificados e acompanhados\nautomaticamente — sem que precises de intervir.",
        details: [
          'Resposta automática ao lead em menos de 2 minutos, 24/7',
          'Qualificação e acompanhamento de leads sem intervenção manual',
          'Integração com o teu CRM, email ou WhatsApp',
        ],
      },
    ],
    cta: 'Falar Connosco →',
  },
  en: {
    label: 'SERVICES',
    headline: 'What we deliver',
    services: [
      {
        titleLine1: 'Landing Pages',
        titleLine2: 'that Convert',
        description:
          "We don't build pretty pages. We build conversion machines — grounded in UX research, cognitive psychology, and tested CRO principles. Every element has a purpose: turning visitors into clients.",
        details: [
          'Ready in 7 to 14 days: copy, design and deployment included',
          'Designed with UX, cognitive psychology and CRO principles',
          'Optimised for speed, mobile and conversion',
        ],
      },
      {
        titleLine1: 'Automation',
        titleLine2: 'that Works for You',
        description:
          "Email, SMS and WhatsApp running 24/7.\nLeads captured, qualified and nurtured\nautomatically — without you having to intervene.",
        details: [
          'Automatic lead response in under 2 minutes, 24/7',
          'Lead qualification and nurturing without manual work',
          'Integrates with your CRM, email or WhatsApp',
        ],
      },
    ],
    cta: 'Talk to Us →',
  },
};

interface ServiceCardProps {
  service: (typeof content.pt.services)[0];
  index: number;
}

function ServiceCard({ service, index }: ServiceCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 180}ms` }}
    >
      <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-3xl p-8 md:p-12 border border-gray-700/30 hover:border-violet-500/30 transition-all duration-500 backdrop-blur-sm">
        {/* Top row: large title left + description right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start mb-10">
          {/* Left — Title */}
          <div>
            <p className="text-white text-3xl md:text-4xl lg:text-5xl font-extralight leading-tight tracking-wide mb-1">
              {service.titleLine1}
            </p>
            <p
              className="text-3xl md:text-4xl lg:text-5xl font-extralight leading-tight tracking-wide bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent"
            >
              {service.titleLine2}
            </p>
          </div>

          {/* Right — Description */}
          <p className="text-white/70 text-base leading-[1.7] font-normal">
            {service.description}
          </p>
        </div>

        {/* Bottom — 3 detail items */}
        <div className="flex flex-col sm:flex-row gap-4">
          {service.details.map((detail, i) => (
            <div
              key={i}
              className="flex items-center gap-3 flex-1 bg-white/5 border border-white/10 rounded-xl px-5 py-3 hover:border-violet-500/40 transition-colors duration-300"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-violet-400 flex-shrink-0"></span>
              <span className="text-white/80 text-sm font-normal">{detail}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ProductSection({ lang }: ProductSectionProps) {
  const t = content[lang];

  return (
    <section id="servicos" className="py-24">
      <div className="container mx-auto px-6">
        {/* Section label + headline */}
        <div className="mb-16">
          <p className="text-violet-400 text-[11px] font-medium tracking-[0.15em] uppercase mb-4">
            {t.label}
          </p>
          <h2 className="text-4xl md:text-[48px] font-light text-white tracking-[0.05em]">
            {t.headline}
          </h2>
        </div>

        {/* Services */}
        <div className="space-y-8">
          {t.services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 flex justify-center">
          <a
            href="#contacto"
            className="group relative bg-transparent border border-white/40 text-white px-10 py-4 rounded-xl text-base font-light transition-all duration-300 hover:border-violet-400 hover:bg-violet-500/10 whitespace-nowrap cursor-pointer tracking-wide"
          >
            {t.cta}
          </a>
        </div>
      </div>
    </section>
  );
}
